// One declaration of a mutating call, read by BOTH the button that fires it and
// the snippet that shows a machine how to fire the same thing.
//
// WHY A DESCRIPTOR AND NOT TWO STRINGS. "What a human can do in the UI, an agent
// can do over MCP, and a system can do over REST" is the platform's promise, and
// `packages/domain/src/capability-manifest.ts` is the seam that states it per
// capability. This module is how a READER checks that claim without reading the
// manifest: the copy button beside a control emits the exact same request the
// control issues. That only holds if the two cannot drift, so the method, the
// path and the body are declared ONCE — here — and `sendApiCall` is what the
// mutation helper calls. A hand-written snippet next to a hand-written fetch is
// two sources for one fact, and the snippet is the one nobody notices going
// stale.
//
// THE MCP HALF IS CHECKED, NOT TRUSTED. A call site spells its verb out because
// the two surfaces do not always agree on a noun (`box.utilization` is
// `box.fleet_status`; `plan.start` is the flat `plan_start`), so it cannot be
// derived from the capability id. `mcpEquivalent` then checks that verb against
// the manifest before anything renders: a renamed or retired verb degrades to a
// visible "no MCP equivalent" rather than printing a call the server would
// answer `-32601` to. `api-call.test.ts` pins that every call this app declares
// IS catalogued, so the degraded path can never ship silently.
//
// NO CREDENTIAL EVER LANDS IN A SNIPPET. The token is `$DEVELOPERZ_API_KEY`, an
// environment reference, matching the shell-history-safe posture the docs take
// (`apps/marketing/content/docs/mcp-clients.mdx`) and `joinOneLiner`'s. The
// dashboard's own credential is an httpOnly cookie it cannot read anyway; a PAT
// is what the copied command needs, and it is the reader's to supply.
//
// `X-Dz-Account` IS DELIBERATELY ABSENT FROM THE REST SNIPPET. The SPA sends it
// on every `/org/<login>` page, but a PAT ignores it on REST: a token minted in
// one account stays bound to that account for life (capability-manifest.ts,
// "KNOWN ASYMMETRY: MULTI-ORG CONTEXT"). Emitting the header would teach a reader
// it does something. The MCP snippet is the other way round: every `tools/call`
// takes the reserved `account` argument (packages/mcp `act-as.ts`), so on an org
// page it NAMES that org — without it the call acts in the credential's own
// account, which on someone's second org is the wrong tenant.

import { CAPABILITY_MANIFEST } from '@developerz/domain';
import { API_BASE_URL, apiFetch } from './api';

/** Every method a mutating control can use. Reads are not copied — nothing to prove. */
export type ApiCallMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A JSON request body, or nothing at all. */
export type ApiCallBody = Readonly<Record<string, unknown>>;

/**
 * The MCP address of the same act, as the call site declares it — the verb AND
 * the argument names its schema declares, which nothing here can cross-check
 * (`mcpEquivalent` checks the verb against the manifest; the arguments are the
 * call site's word). `none` is the honest answer where a REST capability's MCP
 * twin does not reach THIS direction of it. No declared call files `none`
 * today: the repo flip did until `project disable` landed (#4607).
 */
export type McpEquivalent =
  | {
      readonly kind: 'verb';
      /** `resource.action` (`box.drain`) or a flat tool name (`plan_start`). */
      readonly verb: string;
      /** Arguments minus `action`, which is derived from the verb. */
      readonly arguments: ApiCallBody;
    }
  | { readonly kind: 'none'; readonly reason: string };

/** One mutating call: what the button fires and what the snippets describe. */
export type ApiCall = {
  /** A `CAPABILITY_MANIFEST` key — the REST catalog id, a public contract. */
  readonly capability: string;
  readonly method: ApiCallMethod;
  /** The `/v1/…` path exactly as `apiFetch` receives it, already encoded. */
  readonly path: string;
  readonly body?: ApiCallBody;
  readonly mcp: McpEquivalent;
  /**
   * Sent as the `Idempotency-Key` header (#6770), so a retried attempt of ONE submit is
   * one write on the server's ledger (`withIdempotency`). Never in the MCP `arguments`
   * and never printed: `curlSnippet` shows a placeholder, `mcpSnippet` nothing.
   */
  readonly idempotencyKey?: string;
};

/**
 * Fire the call. The ONLY door a `CopyAsCall`-backed mutation goes through, so
 * the request on the wire and the snippet on screen are built from one value.
 */
export function sendApiCall<T>(call: ApiCall, token: string | null): Promise<T> {
  return apiFetch<T>(call.path, token, {
    method: call.method,
    ...(call.body === undefined ? {} : { body: JSON.stringify(call.body) }),
    ...(call.idempotencyKey === undefined
      ? {}
      : { headers: { 'Idempotency-Key': call.idempotencyKey } }),
  });
}

/** What `mcpEquivalent` decided about a call's MCP half. */
export type McpVerdict =
  | { readonly kind: 'verb'; readonly verb: string; readonly arguments: ApiCallBody }
  /** No MCP door for this act, with the reason to render (manifest or call site). */
  | { readonly kind: 'unavailable'; readonly reason: string }
  /** The declared verb is not in the manifest — withheld rather than guessed. */
  | { readonly kind: 'drift' };

/**
 * The call's MCP half, CHECKED against the capability manifest.
 *
 * The manifest is the one place that says whether MCP serves a capability at
 * all, and it is cross-checked against the live MCP catalog by
 * `capability-manifest.test.ts`. So an unknown capability id, a capability the
 * manifest files as `na`/`gap`, and a verb the manifest does not list are three
 * different wrong answers and each is refused rather than printed.
 */
export function mcpEquivalent(call: ApiCall): McpVerdict {
  if (call.mcp.kind === 'none') return { kind: 'unavailable', reason: call.mcp.reason };
  const row = CAPABILITY_MANIFEST[call.capability];
  if (row === undefined) return { kind: 'drift' };
  if (row.mcp.kind !== 'served') return { kind: 'unavailable', reason: row.mcp.reason };
  if (!row.mcp.verbs.includes(call.mcp.verb)) return { kind: 'drift' };
  return call.mcp;
}

/**
 * The JSON-RPC body a `tools/call` carries. A dotted verb is a resource tool
 * whose action rides in `arguments`; an undotted one is a flat tool that takes
 * its arguments straight. `account` (a login or `acc_` id) is the reserved
 * per-call argument every verb accepts; `null` omits it, and the call acts in
 * the credential's own account. `id: 1` because a copied command is a single
 * call, not a session.
 */
export function mcpRequestBody(
  verb: string,
  args: ApiCallBody,
  account: string | null = null,
): ApiCallBody {
  const scoped = account === null ? args : { account, ...args };
  const dot = verb.indexOf('.');
  const params =
    dot === -1
      ? { name: verb, arguments: scoped }
      : { name: verb.slice(0, dot), arguments: { action: verb.slice(dot + 1), ...scoped } };
  return { jsonrpc: '2.0', id: 1, method: 'tools/call', params };
}

/**
 * Wrap a value for a POSIX single-quoted shell argument. A JSON body is pasted
 * inside `'…'`, and prose from a box briefing or a PR title can contain an
 * apostrophe — unescaped, it ends the quote and hands the rest of the JSON to
 * the shell as words. `'\''` is the only way to get one back in.
 */
export function shellSingleQuote(value: string): string {
  return `'${value.replaceAll("'", String.raw`'\''`)}'`;
}

/**
 * Absolute origin the copied command should dial. `PUBLIC_API_URL` is set at
 * build time in prod (`app.` → `api.`); unset means same-origin, which in dev is
 * the vite proxy, so the page's own origin is the right and only answer.
 * Trailing slashes are stripped for the same reason `joinOneLiner` strips them:
 * a configured `https://api.example/` would otherwise yield `//v1/…`.
 */
export function apiOrigin(base: string = API_BASE_URL): string {
  const trimmed = base.replace(/\/+$/, '');
  if (trimmed !== '') return trimmed;
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/** The env var a copied command reads its PAT from. Never a literal token. */
export const TOKEN_ENV_VAR = 'DEVELOPERZ_API_KEY';

/**
 * What a REST snippet prints where an `Idempotency-Key` goes (#6770): a placeholder, never the
 * key the button minted, because a copied command that reused it would be answered from the
 * ledger instead of doing anything.
 */
export const IDEMPOTENCY_KEY_PLACEHOLDER = '<a fresh id per attempt>';

/** `curl` lines shared by both snippets: the bearer, and a JSON body when there is one. */
function curlCommand(
  method: ApiCallMethod,
  url: string,
  body: ApiCallBody | undefined,
  idempotent = false,
): string {
  const lines = [
    `curl -X ${method} ${url}`,
    `  -H "Authorization: Bearer $${TOKEN_ENV_VAR}"`,
    ...(idempotent
      ? [`  -H ${shellSingleQuote(`Idempotency-Key: ${IDEMPOTENCY_KEY_PLACEHOLDER}`)}`]
      : []),
    ...(body === undefined
      ? []
      : [
          '  -H "Content-Type: application/json"',
          `  -d ${shellSingleQuote(JSON.stringify(body))}`,
        ]),
  ];
  return lines.join(' \\\n');
}

/** The REST call as a runnable `curl`. */
export function curlSnippet(call: ApiCall, origin: string = apiOrigin()): string {
  return curlCommand(
    call.method,
    `${origin}${call.path}`,
    call.body,
    call.idempotencyKey !== undefined,
  );
}

/**
 * The MCP call as a runnable `curl`, or null when there is no MCP door. Posts to
 * `/v1/mcp` on the same origin: prod serves this app on both `api.` and `mcp.`,
 * so the endpoint is reachable from whichever one the dashboard was built for.
 * `account` is the page's org (`urlOrg()`), carried as the call's `account`.
 */
export function mcpSnippet(
  call: ApiCall,
  origin: string = apiOrigin(),
  account: string | null = null,
): string | null {
  const verdict = mcpEquivalent(call);
  if (verdict.kind !== 'verb') return null;
  const body = mcpRequestBody(verdict.verb, verdict.arguments, account);
  return curlCommand('POST', `${origin}/v1/mcp`, body);
}
